import { AssignmentType, CREATE_ASSIGNMENT, UPDATE_ASSIGNMENT, useAssignmentService } from 'domain/shared/assignment';
import { useAuthorization } from 'framework/authorization';
import { CreateEditFormDrawer } from 'framework/ViewTypes/contentLayout';
import useFeatureToggle from 'framework/featureToggle';
import { Features } from 'infrastructure/featureToggle';
import AssignmentDrawerFields from './AssignmentDrawerFields';
import AssignmentMembershipFields from './AssignmentMembershipFields';
import AssignmentContext from '../AssignmentContext';
import { AssignmentDrawerMode } from './types';

interface AssignmentFormDrawerProps {
  mode: AssignmentDrawerMode;
  assignmentId?: string;
  onClose: () => void;
  onSuccess: () => void | Promise<void>;
}

const AssignmentFormDrawerContent = ({ mode, assignmentId, onClose, onSuccess }: AssignmentFormDrawerProps) => {
  const { create, update, getAssignmentById, getValidationSchema } = useAssignmentService();
  const isEdit = mode === AssignmentDrawerMode.EDIT;
  const { isFeatureEnabled } = useFeatureToggle();
  const isMembershipEnabled = isFeatureEnabled(Features.ASSIGNMENT_TEAM_MEMBERSHIP);

  return (
    <CreateEditFormDrawer
      isOpen
      onClose={onClose}
      title={isEdit ? 'Edit assignment' : mode === AssignmentDrawerMode.COPY ? 'Copy assignment' : 'Create assignment'}
      width={864}
      initialData={{ assignmentType: AssignmentType.INDIVIDUAL_ASSIGNMENT }}
      getDataFunc={assignmentId ? () => getAssignmentById(assignmentId) : undefined}
      submitFormFunc={data => (isEdit ? update(assignmentId!, data) : create(data))}
      isCreateRequest={!isEdit}
      getValidationSchemaFunc={getValidationSchema}
      submitButtonTitle={isEdit ? 'Save' : 'Create'}
      afterSubmit={{
        execute: async () => {
          await onSuccess();
          onClose();
        },
        message: isEdit
          ? 'The Assignment has been successfully saved.'
          : 'The Assignment has been successfully created.',
      }}
      getSections={data => [
        {
          id: 'assignment',
          title: 'Assignment information',
          content: <AssignmentDrawerFields isCreateForm={!isEdit} />,
        },
        {
          id: 'membership',
          title: isMembershipEnabled && data.target?.type === 'PROJECT' ? 'Membership information' : undefined,
          content: <AssignmentMembershipFields isCreateForm={!isEdit} />,
        },
      ]}
    />
  );
};

const AssignmentFormDrawer = (props: AssignmentFormDrawerProps) => {
  const { isGranted } = useAuthorization();

  if (props.mode === AssignmentDrawerMode.EDIT) {
    return (
      <AssignmentContext key={props.assignmentId} id={props.assignmentId} permissionToCheck={UPDATE_ASSIGNMENT}>
        <AssignmentFormDrawerContent {...props} />
      </AssignmentContext>
    );
  }

  if (!isGranted(CREATE_ASSIGNMENT)) return null;

  return <AssignmentFormDrawerContent {...props} />;
};

export default AssignmentFormDrawer;
